import z from 'zod';
import { conditionOperatorValues, screenActionValues, screens } from '@/db/schema';
import { queryParamsSchema } from '../core/schemas';

const screenColumns = Object.keys(screens ?? {}) as [
  keyof typeof screens,
  ...(keyof typeof screens)[],
];

const fieldTypeEnum = z.enum([
  'string',
  'password',
  'number',
  'currency',
  'boolean',
  'date',
  'datetime',
  'enum',
  'relation',
  'multi-relation',
  'component',
  'file',
  'avatar',
  'email',
  'phone',
]);

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const relationSchema = z.object({
  resource: z.string(),
  displayField: z.string(),
  orderBy: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

const conditionSchema = z.object({
  field: z.string(),
  operator: z.enum(conditionOperatorValues),
  value: z.string().optional(),
});

const columnDefSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: fieldTypeEnum,
  sortable: z.boolean().optional(),
  order: z.number().optional(),
  relation: relationSchema.optional(),
  component: z.string().optional(),
  mask: z.array(z.string()).optional(),
  options: z.array(optionSchema).optional(),
});

const filterDefSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'select', 'date', 'period']),
  order: z.number().optional(),
  options: z.array(optionSchema).optional(),
  relation: relationSchema.optional(),
  defaultValue: z.union([z.string(), z.object({ nextBy: z.string() })]).optional(),
});

const fieldDefSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: fieldTypeEnum,
  required: z.boolean().optional(),
  width: z.number().optional(),
  colSpan: z
    .object({
      sm: z.number().optional(),
      md: z.number().optional(),
      lg: z.number().optional(),
    })
    .optional(),
  order: z.number().optional(),
  section: z.string().optional(),
  options: z.array(optionSchema).optional(),
  relation: relationSchema.optional(),
  component: z.string().optional(),
  accept: z.string().optional(),
  avatarPreview: z.boolean().optional(),
  mask: z.array(z.string()).optional(),
  showOnCreate: z.boolean().optional(),
  showOnEdit: z.boolean().optional(),
  showOnView: z.boolean().optional(),
  disabledOnEdit: z.boolean().optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
  visibleIf: z.array(conditionSchema).optional(),
  enabledIf: z.array(conditionSchema).optional(),
});

const layoutSchema = z.object({
  columns: z.array(columnDefSchema).default([]),
  filters: z.array(filterDefSchema).default([]),
  fields: z.array(fieldDefSchema).default([]),
  perPage: z.number().int().positive().optional(),
});

export const screenSchemas = {
  queryParams: queryParamsSchema.extend({
    orderBy: z
      .enum(
        screenColumns,
        `Campo inválido! Deve ser um dos seguintes: ${screenColumns.join(', ')}`,
      )
      .optional(),
    id: z.string().optional(),
    resource: z.string().optional(),
    isActive: z.string().optional(),
    createdAt: z.coerce.date().optional(),
    createdAtFrom: z.coerce.date().optional(),
    createdAtTo: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
    updatedAtFrom: z.coerce.date().optional(),
    updatedAtTo: z.coerce.date().optional(),
  }),
  create: z.object({
    resource: z
      .string('Campo obrigatório!')
      .min(1, 'Campo obrigatório!')
      .regex(/^[a-z][a-z0-9-]*$/, 'Use apenas letras minúsculas, números e hífen.'),
    label: z.string('Campo obrigatório!').min(1, 'Campo obrigatório!'),
    pluralLabel: z.string('Campo obrigatório!').min(1, 'Campo obrigatório!'),
    icon: z.string().optional(),
    menuOrder: z.number().optional().default(0),
    menuGroup: z.string().optional(),
    showInMenu: z.boolean().optional().default(true),
    actions: z
      .array(z.enum(screenActionValues))
      .optional()
      .default([...screenActionValues]),
    layout: layoutSchema,
    isActive: z.boolean().optional().default(true),
  }),
  update: z.object({
    resource: z.any().readonly().optional(),
    label: z.string().min(1, 'Campo obrigatório!').optional(),
    pluralLabel: z.string().min(1, 'Campo obrigatório!').optional(),
    icon: z.string().optional(),
    menuOrder: z.number().optional(),
    menuGroup: z.string().optional(),
    showInMenu: z.boolean().optional(),
    actions: z.array(z.enum(screenActionValues)).optional(),
    layout: layoutSchema.optional(),
    isActive: z.boolean().optional(),
  }),
};

export type ScreenQueryParams = z.infer<typeof screenSchemas.queryParams>;
export type CreateScreenSchema = z.input<typeof screenSchemas.create>;
export type UpdateScreenSchema = z.infer<typeof screenSchemas.update>;
